<template>
  <smart-filters
    v-model="filters"
    :schema="filterSchemaActual"
    :default-filters="defaultFilters"
    storage-key="reports"
    @filter="onFilter"
    @clear="onClear"
  />
</template>

<script setup>
import {
  ref,
  computed,
  watch,
} from 'vue';
import SmartFilters from '@/shared/components/smart_filters.vue';
import ReporteService from '@/services/reporte_service';
import { resolveApiUrl } from '@/shared/utils/resolve_api_url.js';
import AtributosDinamicosService from '@/services/atributos_dinamicos_service';
import {
  reportsFiltersConfig,
  reportsFiltersValidations,
} from '../models/reports_filters_config.js';
import { initial_filters } from '../models/reports_model.js';
import { useLoading } from '@/shared/composables/loading.js';
import { ElNotification } from 'element-plus';
import {
  getParamsUrlCriteria,
  mapAtributosDinamicosToFilters,
} from '@/shared/criteria/criteria_utils.js';

const props = defineProps({
  reporteSeleccionado: {
    type: Object,
    required: true,
  },
});

const filters = ref({ ...initial_filters });

const filterSchemaActual = ref([]);

const getAtributosDinamicos = async () => {
  try {
    return await AtributosDinamicosService.getDefinicionesFiltrosCriteria();
  } catch (error) {
    ElNotification({
      title: 'Error',
      message: 'Error al cargar atributos dinámicos',
      type: 'error',
      duration: 3000,
    });
    return [];
  }
};

watch(
  props.reporteSeleccionado,
  async () => {
    const filters = reportsFiltersConfig[props.reporteSeleccionado.Nombre] || [];

    if (props.reporteSeleccionado.Nombre === 'listado_de_existencias') {
      const atributos = await getAtributosDinamicos();
      if (atributos && atributos.length > 0) {
        const filtersFromAtributos = mapAtributosDinamicosToFilters(atributos);
        filtersFromAtributos.forEach((f) => {
          if (f.type === 'date_range' && f.keyDesde && f.keyHasta) {
            if (!filters.find((i) => i.keyDesde === f.keyDesde && i.keyHasta === f.keyHasta)) {
              filters.push(f);
            }
          } else if (f.type === 'select_input' && f.keySelect && f.keyInput) {
            if (!filters.find((i) => i.keySelect === f.keySelect && i.keyInput === f.keyInput)) {
              filters.push(f);
            }
          } else {
            if (!filters.find((i) => i.key === f.key)) {
              filters.push(f);
            }
          }
        });
      }
    }
    filterSchemaActual.value = filters.sort((a, b) => a.label.localeCompare(b.label));
  },
  { immediate: true },
);

const validationsSchemaActual = computed(() => {
  if (!props.reporteSeleccionado) return [];
  return reportsFiltersValidations[props.reporteSeleccionado.Nombre] || [];
});

const defaultFilters = computed(() => initial_filters);

const onFilter = async () => {
  if (filters.value && validationsSchemaActual.value.length > 0) {
    const validationErrors = validationsSchemaActual.value
      .map((validation) => {
        if (!filters.value[validation.key]) {
          return `${validation.message} <br />`;
        }
        return null;
      })
      .filter((error) => error !== null);

    if (validationErrors.length > 0) {
      ElNotification({
        title: 'Validación',
        message: validationErrors.join(''),
        dangerouslyUseHTMLString: true,
        type: 'warning',
      });
      return;
    }
  }

  try {
    useLoading().openLoading('Generando reporte...');
    let localFilters = {};
    if (filters.value['Puntos']) {
      const puntos = filters.value['Puntos'];

      localFilters = JSON.parse(JSON.stringify(filters.value));
      delete localFilters['Puntos'];
      localFilters[puntos.value.Codigo] = {
        field: { ...JSON.parse(JSON.stringify(puntos.field)), key: puntos.value.Codigo },
        value: null,
      };
    } else {
      localFilters = JSON.parse(JSON.stringify(filters.value));
    }

    const result = await ReporteService.generateReportWithCriteria(
      props.reporteSeleccionado.Nombre,
      getParamsUrlCriteria(localFilters),
    );

    if (result && result.data && result.data.success) {
      const reportURL = resolveApiUrl(result.data.url);
      window.open(reportURL, '_blank');
      ElNotification({
        title: 'Éxito',
        message: 'Informe generado exitosamente',
        type: 'success',
      });
    } else {
      ElNotification({
        title: 'Error',
        message: 'Hubo un problema al generar el informe.',
        type: 'error',
      });
    }
  } catch (error) {
    ElNotification({
      title: 'Error',
      message: error.message || 'Hubo un problema al generar el informe.',
      type: 'error',
    });
  } finally {
    useLoading().closeLoading();
  }
};

const onClear = () => {
  filters.value = { ...initial_filters };
};
</script>

<style scoped></style>
